import React, { useEffect, useState } from 'react';
import { AdminOrgSummary } from '../../lib/types';
import { adminListOrgs } from '../../lib/api';
import { EmptyState, SkeletonRow } from '../ui';
import { AdminOrgDetail } from './AdminOrgDetail';

interface Props {
  showToast: (msg: string, type?: 'success' | 'error') => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

function daysRemaining(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

export function AdminOrgsPage({ showToast }: Props) {
  const [orgs, setOrgs] = useState<AdminOrgSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);

  const fetchOrgs = async () => {
    setLoading(true);
    setErrored(false);
    const res = await adminListOrgs();
    if (res.ok && res.data) {
      setOrgs(res.data);
    } else {
      setErrored(true);
      showToast(res.message || 'Failed to load orgs', 'error');
    }
    setLoading(false);
  };

  useEffect(() => { fetchOrgs(); }, []);

  const headerCell: React.CSSProperties = {
    padding: 'var(--sp-3)',
    fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text2)',
    textAlign: 'left', textTransform: 'uppercase', letterSpacing: .4,
    background: 'var(--surface2)', borderBottom: '1px solid var(--border)',
  };
  const cell: React.CSSProperties = {
    padding: 'var(--sp-3)', fontSize: 'var(--fs-base)', borderBottom: '1px solid var(--border)',
  };

  return (
    <div className="pane-enter">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xl font-bold">Organisations</div>
          <div className="text-sm text-muted mt-1">
            {loading ? 'Loading…' :
              errored ? 'Failed to load' :
                `${orgs.length} organisation${orgs.length !== 1 ? 's' : ''} using GoPal`}
          </div>
        </div>
        <button className="btn btn-secondary" onClick={fetchOrgs}>
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.4">
            <path d="M14 8a6 6 0 11-1.76-4.24M14 3v3.5h-3.5" />
          </svg>
          Refresh
        </button>
      </div>

      {errored && !loading ? (
        <EmptyState
          tone="error"
          title="Couldn't load organisations"
          description="The server returned an error. Check your connection and try again."
          action={{ label: 'Retry', onClick: fetchOrgs }}
        />
      ) : (
        <div className="card card-flush" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerCell}>Org</th>
                <th style={headerCell}>Status</th>
                <th style={headerCell}>Seats</th>
                <th style={headerCell}>Active 30d</th>
                <th style={headerCell}>Created</th>
                <th style={{ ...headerCell, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  <SkeletonRow cols={6} />
                  <SkeletonRow cols={6} />
                  <SkeletonRow cols={6} />
                </>
              ) : orgs.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 0 }}>
                    <EmptyState
                      title="No organisations yet"
                      description="Use the Onboard tab above to add your first customer."
                    />
                  </td>
                </tr>
              ) : orgs.map(o => {
                const isExpanded = expandedOrg === o.id;
                const seatPct = o.max_users > 0 ? (o.user_count / o.max_users) : 0;
                const seatColor = seatPct >= 1 ? 'var(--red)' : seatPct >= 0.8 ? 'var(--amber)' : 'var(--text)';
                const isTrial = o.subscription_status === 'trial';
                const days = isTrial && o.trial_ends_at ? daysRemaining(o.trial_ends_at) : null;
                const statusColor = o.subscription_status === 'active' ? 'var(--teal)' :
                  o.subscription_status === 'trial' ? (days !== null && days <= 5 ? 'var(--amber)' : 'var(--text2)') :
                  'var(--red)';
                const statusBg = o.subscription_status === 'active' ? 'var(--teal-bg)' :
                  o.subscription_status === 'trial' ? (days !== null && days <= 5 ? 'var(--amber-bg)' : 'var(--surface2)') :
                  'var(--red-bg)';

                return (
                  <React.Fragment key={o.id}>
                    <tr
                      onClick={() => setExpandedOrg(isExpanded ? null : o.id)}
                      style={{ cursor: 'pointer', background: isExpanded ? 'var(--surface2)' : 'transparent' }}
                    >
                      <td style={cell}>
                        <div className="font-medium">{o.name}</div>
                        <div className="text-xs text-subtle mt-1">{o.slug}</div>
                      </td>
                      <td style={cell}>
                        <span className="badge" style={{ background: statusBg, color: statusColor }}>
                          {o.subscription_status === 'trial' ? (
                            days !== null ? `TRIAL · ${days}d` : 'TRIAL'
                          ) : o.subscription_status.toUpperCase()}
                        </span>
                      </td>
                      <td style={cell}>
                        <span className="text-mono font-semibold" style={{ color: seatColor }}>
                          {o.user_count}/{o.max_users}
                        </span>
                      </td>
                      <td style={cell}><span className="text-mono">{o.active_30d}</span></td>
                      <td style={cell}>{formatDate(o.created_at)}</td>
                      <td style={cell}>
                        <span className="text-subtle">{isExpanded ? '▾' : '▸'}</span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0, background: 'var(--surface2)' }}>
                          <AdminOrgDetail
                            orgId={o.id}
                            org={o}
                            showToast={showToast}
                            onChanged={fetchOrgs}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
